import React, { useEffect, useRef } from 'react';

import { IBoxConstraints, IPoint, WhichPlate } from './interfaces';

interface IProps {
  label: WhichPlate;
  boxConstraints: IBoxConstraints;
  inverse?: boolean;
}

export function Plate(props: IProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();

    ctx.strokeStyle = props.inverse ? 'rgba(0, 0, 0, 0.31)' : 'rgba(255, 255, 255, 0.31)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    ctx.strokeRect(
      0,
      0,
      props.boxConstraints.maxWidth,
      props.boxConstraints.maxHeight
    );
    ctx.closePath();
    ctx.font = '10px Nunito Sans, sans-serif';
    ctx.fillStyle = props.inverse ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)';
    drawPlateLabel(ctx, props.label, props.boxConstraints.maxWidth);
    drawAnteriorLabel(
      ctx,
      props.label,
      props.boxConstraints.maxWidth,
      props.boxConstraints.maxHeight
    );
    drawPosteriorLabel(
      ctx,
      props.label,
      props.boxConstraints.maxWidth,
      props.boxConstraints.maxHeight
    );
    drawMedialLabel(
      ctx,
      props.label,
      props.boxConstraints.maxWidth,
      props.boxConstraints.maxHeight
    );
    drawLateralLabel(
      ctx,
      props.label,
      props.boxConstraints.maxWidth,
      props.boxConstraints.maxHeight
    );
  }, [props.label, props.boxConstraints, props.inverse]);

  return (
    <canvas
      style={{ position: 'absolute', top: 0, left: 0 }}
      ref={canvasRef}
      id={`${props.label}-plate`}
      width={`${props.boxConstraints.maxWidth}`}
      height={`${props.boxConstraints.maxHeight}`}
    />
  );
}
/**
 * @description Draws the rotated text
 * @param ctx
 * @param text
 * @param pivot
 * @param angle
 */
const drawRotatedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  pivot: IPoint,
  angle: number
) => {
  ctx.save();
  ctx.translate(pivot.x, pivot.y);
  ctx.rotate(angle);
  ctx.fillText(text, 0, 0);
  ctx.restore();
};

/**
 * @description Draws the label on the plate
 * @param ctx
 * @param label
 * @param width
 */
const drawPlateLabel = (
  ctx: CanvasRenderingContext2D,
  label: string,
  width: number
) => {
  ctx.save();
  ctx.textBaseline = 'bottom';
  const textMeasurements = ctx.measureText(label);

  const offset = {
    x: width / 2 - textMeasurements.width / 2,
    y: textMeasurements.actualBoundingBoxAscent + 2,
  };

  ctx.translate(offset.x, offset.y);
  ctx.fillText(label, 0, 0);
  ctx.restore();
};

/**
 * @description Draws the Anterior label on the plate
 * @param ctx
 * @param whichPlate
 * @param width
 * @param height
 */
const drawAnteriorLabel = (
  ctx: CanvasRenderingContext2D,
  whichPlate: WhichPlate,
  width: number,
  height: number
) => {
  const label = 'Anterior';
  ctx.textBaseline = 'bottom';
  const textMeasurements = ctx.measureText(label);
  const offset =
    whichPlate === WhichPlate.left
      ? {
          x: 0,
          y: height / 2 / 2 - textMeasurements.width / 2,
        }
      : {
          x: width,
          y: height / 2 / 2 + textMeasurements.width / 2,
        };
  drawRotatedText(
    ctx,
    label,
    offset,
    Math.PI * (whichPlate === WhichPlate.left ? 0.5 : 1.5)
  );
};
/**
 * @description Draws the Posterior label on the plate
 * @param ctx
 * @param whichPlate
 * @param width
 * @param height
 */
const drawPosteriorLabel = (
  ctx: CanvasRenderingContext2D,
  whichPlate: WhichPlate,
  width: number,
  height: number
) => {
  const label = 'Posterior';
  ctx.textBaseline = 'bottom';
  const textMeasurements = ctx.measureText(label);
  const offset =
    whichPlate === WhichPlate.left
      ? {
          x: 0,
          y: height * 0.75 - textMeasurements.width / 2,
        }
      : {
          x: width,
          y: height * 0.75 + textMeasurements.width / 2,
        };
  drawRotatedText(
    ctx,
    label,
    offset,
    Math.PI * (whichPlate === WhichPlate.left ? 0.5 : 1.5)
  );
};

/**
 * @description Draws the Medial label on the plate
 * @param ctx
 * @param whichPlate
 * @param width
 * @param height
 */
const drawMedialLabel = (
  ctx: CanvasRenderingContext2D,
  whichPlate: WhichPlate,
  width: number,
  height: number
) => {
  const label = 'Medial';
  ctx.textBaseline = 'bottom';
  const textMeasurements = ctx.measureText(label);
  const offset =
    whichPlate === WhichPlate.left
      ? {
          x: width * 0.75 - textMeasurements.width / 2,
          y: height,
        }
      : {
          x: width * 0.25 - textMeasurements.width / 2,
          y: height,
        };
  ctx.fillText(label, offset.x, offset.y);
};

/**
 * @description Draws the Lateral label on the plate
 * @param ctx
 * @param whichPlate
 * @param width
 * @param height
 */
const drawLateralLabel = (
  ctx: CanvasRenderingContext2D,
  whichPlate: WhichPlate,
  width: number,
  height: number
) => {
  const label = 'Lateral';
  ctx.textBaseline = 'bottom';
  const textMeasurements = ctx.measureText(label);
  const offset =
    whichPlate === WhichPlate.left
      ? {
          x: width * 0.25 - textMeasurements.width / 2,
          y: height,
        }
      : {
          x: width * 0.75 - textMeasurements.width / 2,
          y: height,
        };
  ctx.fillText(label, offset.x, offset.y);
};
